import { createEvent, sample, Store, combine } from 'effector'
import { interviewFixturesService } from '../../interview-fixtures.service'
import { GtmEventData } from 'utils/analytics'
import { analyticsService } from 'features/analytics.service'
import { callWsStateService } from './call-ws-state.service'
import { doCaptureSentryError, SentryErrorInfo } from 'utils/sentry'
import { finishCallModalService } from './finish-call-modal/finish-call-modal.service'

type ExtractStoreValue<S> = S extends Store<infer T> ? T : never

export function combineNonFalsy<T extends Record<string, Store<unknown>>>(
  stores: T
): Store<{ [K in keyof T]: NonNullable<ExtractStoreValue<T[K]>> } | null> {
  return combine(stores, (values) => {
    if (Object.values(values).some((v) => !v)) return null
    return values as { [K in keyof T]: NonNullable<ExtractStoreValue<T[K]>> }
  })
}

class CallAnalyticsService {
  readonly interviewUserAnswered = createEvent<string>()
  readonly interviewBotAnswered = createEvent<string>()
  readonly callStarted = createEvent<void>()
  readonly callPageOpen = createEvent<void>()
  readonly callFinished = createEvent<void>()
  readonly interviewUserConnected = createEvent<void>()
  readonly webSocketConnectinError = createEvent<Event>()

  constructor() {
    sample({
      clock: this.callPageOpen,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: ({ vacancy, interview }): GtmEventData<'page_call_open'> => {
        return {
          event: 'page_call_open',
          vacancy_id: vacancy.id,
          interview_id: interview.id
        }
      },
      target: analyticsService.analyticsHappened
    })

    sample({
      clock: this.interviewUserAnswered,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: (
        { vacancy, interview },
        text
      ): GtmEventData<'interview_user_answered'> => {
        return {
          event: 'interview_user_answered',
          vacancy_id: vacancy.id,
          interview_id: interview.id,
          user_message: text,
          user_message_length: text.length
        }
      },
      target: analyticsService.analyticsHappened
    })

    sample({
      clock: this.interviewBotAnswered,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: (
        { vacancy, interview },
        text
      ): GtmEventData<'interview_bot_answered'> => {
        return {
          event: 'interview_bot_answered',
          vacancy_id: vacancy.id,
          interview_id: interview.id,
          bot_message: text,
          bot_message_length: text.length
        }
      },
      target: analyticsService.analyticsHappened
    })

    sample({
      clock: this.callStarted,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: ({ vacancy, interview }): GtmEventData<'interview_call_started'> => {
        return {
          event: 'interview_call_started',
          vacancy_id: vacancy.id,
          interview_id: interview.id
        }
      },
      target: analyticsService.analyticsHappened
    })

    sample({
      clock: this.interviewUserConnected,
      source: combineNonFalsy({
        state: interviewFixturesService.$state,
        time: callWsStateService.$initialConnectTime
      }),
      filter: Boolean,
      fn: ({
        state: { vacancy, interview },
        time
      }): GtmEventData<'interview_user_connected'> => {
        const timeToConnect = Date.now() - time
        return {
          event: 'interview_user_connected',
          vacancy_id: vacancy.id,
          interview_id: interview.id,
          user_call_time_to_connect: timeToConnect
        }
      },
      target: analyticsService.analyticsHappened
    })

    sample({
      clock: this.callFinished,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: ({ vacancy, interview }): GtmEventData<'interview_call_finished'> => {
        return {
          event: 'interview_call_finished',
          vacancy_id: vacancy.id,
          interview_id: interview.id
        }
      },
      target: analyticsService.analyticsHappened
    })

    sample({
      clock: this.webSocketConnectinError,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: ({ vacancy, interview }, event): SentryErrorInfo => {
        console.error('ws error: ', event)

        return {
          error: 'Websocket connection error',
          context: {
            vacancy_id: vacancy.id,
            interview_id: interview.id,
            error: event.toString()
          }
        }
      },
      target: doCaptureSentryError
    })

    sample({
      clock: finishCallModalService.open,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: ({
        vacancy,
        interview
      }): GtmEventData<'interview_call_exit_clicked'> => {
        return {
          event: 'interview_call_exit_clicked',
          vacancy_id: vacancy.id,
          interview_id: interview.id
        }
      },
      target: analyticsService.analyticsHappened
    })
  }

  subscribe = (): VoidFunction => {
    return () => void 0
  }
}

export const callAnalyticsService = new CallAnalyticsService()
